import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import '../../styles/Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const VentasChart = ({ data = [], dataByFarmacia = [], type = 'line', title = 'Ventas' }) => {
  // Colores para cada farmacia
  const farmaciaColors = [
    { border: 'rgb(255, 99, 132)', bg: 'rgba(255, 99, 132, 0.1)' },   // Rojo
    { border: 'rgb(54, 162, 235)', bg: 'rgba(54, 162, 235, 0.1)' },   // Azul
    { border: 'rgb(255, 206, 86)', bg: 'rgba(255, 206, 86, 0.1)' },   // Amarillo
    { border: 'rgb(75, 192, 192)', bg: 'rgba(75, 192, 192, 0.1)' },   // Verde
    { border: 'rgb(153, 102, 255)', bg: 'rgba(153, 102, 255, 0.1)' }, // Púrpura
    { border: 'rgb(255, 159, 64)', bg: 'rgba(255, 159, 64, 0.1)' }    // Naranja
  ];

  const chartData = useMemo(() => {
    // Si hay datos por farmacia, usar ese formato
    if (dataByFarmacia && dataByFarmacia.length > 0) {
      // Obtener todas las fechas únicas
      const allDates = new Set();
      dataByFarmacia.forEach(farmacia => {
        farmacia.datos.forEach(item => {
          allDates.add(item.dia);
        });
      });
      
      const sortedDates = Array.from(allDates).sort();
      
      // Agrupar datos por períodos de 15 días
      const groupedData = new Map();
      sortedDates.forEach(dateStr => {
        const date = new Date(dateStr);
        const day = date.getDate();
        const period = day <= 15 ? 1 : 16;
        const periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(period).padStart(2, '0')}`;
        
        if (!groupedData.has(periodKey)) {
          groupedData.set(periodKey, {
            dates: [],
            label: period === 1
              ? `${date.toLocaleDateString('es-CL', { month: 'short' })} 1-15`
              : `${date.toLocaleDateString('es-CL', { month: 'short' })} 16-${new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()}`
          });
        }
        groupedData.get(periodKey).dates.push(dateStr);
      });
      
      const periods = Array.from(groupedData.keys()).sort();
      
      // Crear datasets para cada farmacia
      const datasets = dataByFarmacia.map((farmacia, index) => {
        const color = farmaciaColors[index % farmaciaColors.length];
        
        // Crear un mapa de fecha -> total para esta farmacia
        const dataMap = new Map();
        farmacia.datos.forEach(item => {
          dataMap.set(item.dia, item.total);
        });
        
        // Sumar ventas por período de 15 días
        const dataArray = periods.map(periodKey => {
          const periodDates = groupedData.get(periodKey).dates;
          return periodDates.reduce((sum, date) => sum + (dataMap.get(date) || 0), 0);
        });
        
        return {
          label: farmacia.farmacia_nombre,
          data: dataArray,
          borderColor: color.border,
          backgroundColor: type === 'line' ? color.bg : color.border.replace('rgb', 'rgba').replace(')', ', 0.6)'),
          tension: 0.4,
          fill: type === 'line',
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: color.border,
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        };
      });
      
      return {
        labels: periods.map(periodKey => groupedData.get(periodKey).label),
        datasets
      };
    }
    
    // Formato original (una sola línea)
    if (!data || data.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    return {
      labels: data.map(item => {
        const dateStr = item.dia || item.fecha || item.label;
        if (!dateStr) return '';
        
        try {
          const date = new Date(dateStr);
          return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
        } catch {
          return dateStr;
        }
      }),
      datasets: [
        {
          label: 'Ventas',
          data: data.map(item => item.total || item.value || 0),
          borderColor: 'rgb(102, 126, 234)',
          backgroundColor: type === 'line'
            ? 'rgba(102, 126, 234, 0.1)'
            : 'rgba(102, 126, 234, 0.6)',
          tension: 0.4,
          fill: type === 'line',
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: 'rgb(102, 126, 234)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        }
      ]
    };
  }, [data, dataByFarmacia, type]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: title
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('es-CL', {
                style: 'currency',
                currency: 'CLP'
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return new Intl.NumberFormat('es-CL', {
              style: 'currency',
              currency: 'CLP',
              notation: 'compact'
            }).format(value);
          }
        }
      }
    }
  };

  if ((!data || data.length === 0) && (!dataByFarmacia || dataByFarmacia.length === 0)) {
    return (
      <div className="dashboard-widget">
        <h3 className="widget-title">{title}</h3>
        <div className="widget-empty">
          <p>No hay datos de ventas disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-widget">
      <h3 className="widget-title">
        <span className="widget-icon">📊</span>
        {title}
      </h3>
      <div className="widget-body">
        <div className="chart-container">
          {type === 'line' ? (
            <Line data={chartData} options={options} />
          ) : (
            <Bar data={chartData} options={options} />
          )}
        </div>
      </div>
    </div>
  );
};

export default VentasChart;

// Made with Bob
